import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { addPipelineToAnalyzeRequest } from './canvasUrlParams';
import { isPublicSlug, fetchAndStorePublicToken } from './publicUtils';
import { refreshAuthToken, refreshAuthTokenWithOutcome, isAuthTokenExpiring, notifySessionDisconnected } from './tokenRefresh';
import { ScanMode } from './scanMode';
import { buildAnalyzeFormData, buildAnalyzeZipFormData } from './analyzePayload';

/**
 * Customized Axios request config to include metadata for auth retry logic.
 */
export interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
}

// =============================================================================
// Types for Repository Update Tracking & Commit Diff APIs
// =============================================================================

export interface RepoUpdateStatus {
  repo_url: string;
  has_updates: boolean;
  current_commit: string | null;
  latest_commit: string | null;
  /** When present (e.g. GitHub), number of new commits since last scan. null when unknown (e.g. Bitbucket). */
  new_commits_count?: number | null;
  diff_overlay_available: boolean;
  error: string | null;
}

export interface CheckRepoUpdatesResponse {
  success: boolean;
  repos: RepoUpdateStatus[];
  error: string | null;
}

export interface FileDiffInfo {
  path: string;
  diff_url: string;
  patch?: string;
  /** One-line LLM summary of what changed in this specific file. */
  summary?: string;
  suggestions?: CodeSuggestion[];
}

export interface NodeDiffDetails {
  added: number;
  deleted: number;
  files: FileDiffInfo[];
}

export interface NodeDiffInfo {
  id: string;
  change_type: "add" | "remove" | "update";
  type?: string;
  label?: string;
  info?: string;
  diff_details?: NodeDiffDetails;
}

// Edge info for new edges (full info needed to render)
export interface EdgeInfo {
  id: string;           // Edge ID
  source: string;       // Source node ID
  target: string;       // Target node ID
  label: string | null; // e.g., "HTTP", "invokes", "reads"
}

// Edge changes structure (when edges is not null)
export interface EdgeChanges {
  new: EdgeInfo[];      // Full info - frontend needs to render new edges
  removed: string[];    // IDs only - frontend removes by ID
  updated: string[];    // IDs only - edges where source/target code changed (highlight)
}

export interface GraphChanges {
  nodes: NodeDiffInfo[];
  // Currently always null - frontend derives touched edges from changed nodes
  // When populated in future: { new: [...], removed: [...] }
  edges: EdgeChanges | null;
}

export interface DiffSummary {
  base_commit: string;
  head_commit: string;
  compare_url: string;
  unmapped_files: string[];
  total_files_changed: number;
  total_additions: number;
  total_deletions: number;
  reviews?: NodeReviewSuggestions[];
}

export interface CodeSuggestion {
  title: string;
  description: string;
  severity: "low" | "medium" | "high";
  category: "bug" | "security" | "performance" | "readability" | "other";
  example_code?: string;
}

export interface FileSuggestions {
  path: string;
  /** One-line LLM summary of what changed in this specific file. */
  summary?: string;
  suggestions: CodeSuggestion[];
}

export interface NodeReviewSuggestions {
  node_id: string;
  summary: string;
  files: FileSuggestions[];
}

export interface CommitDiffResponse {
  graph_changes: GraphChanges;
  summary: DiffSummary;
  reviews?: NodeReviewSuggestions[];
  total_suggestions?: number;
}

export type PrLifecycleState = "OPEN" | "MERGED" | "CLOSED";
export type PrDiffFreshnessState = "FRESH" | "STALE" | "COMPUTING" | "FAILED";

export interface PrDiffResponse {
  pr_lifecycle_state: PrLifecycleState;
  diff_freshness_state: PrDiffFreshnessState;
  status_message: string;
  last_updated: string;
  graph_changes: GraphChanges | null;
  summary: DiffSummary | null;
  reviews?: NodeReviewSuggestions[];
  total_suggestions?: number;
}

export interface PersistedPrDiffOption {
  repo_url: string;
  pr_number: number;
  pr_lifecycle_state: PrLifecycleState;
  diff_freshness_state: PrDiffFreshnessState;
  last_updated: string;
}

export interface PersistedPrDiffOptionsResponse {
  options: PersistedPrDiffOption[];
}

// =============================================================================
// Types & API for Code Rescan Mode (backend uses uppercase)
// =============================================================================

export enum CodeRescanMode {
  AUTO = "AUTO",
  MANUAL = "MANUAL",
}

export interface UpdateRescanModeResponse {
  code_rescan_mode: CodeRescanMode;
}

export interface UpdateRepoTokenResponse {
  success: boolean;
  message: string;
}

export interface TriggerRescanResponse {
  success: boolean;
  activities_created: number;
  message: string;
  rescan_already_in_progress?: boolean;
  auth_error_repo_urls?: string[] | null;
}

/** Parse backend value (assume uppercase; accept any case) to enum. */
export function parseCodeRescanMode(raw: unknown): CodeRescanMode {
  if (
    raw === CodeRescanMode.AUTO ||
    (typeof raw === "string" && raw.toUpperCase() === "AUTO")
  ) {
    return CodeRescanMode.AUTO;
  }
  return CodeRescanMode.MANUAL;
}

/** Human-readable label for UI. */
export function codeRescanModeLabel(mode: CodeRescanMode): string {
  return mode === CodeRescanMode.AUTO ? "Auto" : "Manual";
}

const api: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_SIMPLIFAI_BACKEND_API_URL}`, // Replace with your backend API base URL
});

const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";

// Request interceptor for adding the Authorization header
api.interceptors.request.use(async (config: CustomInternalAxiosRequestConfig) => {
  const isPublicRoute = config.url?.includes('/api/get_public_token');
  config.params = addPipelineToAnalyzeRequest(
    config.method,
    config.url,
    config.params,
    window.location.search,
  );

  // If we are about to use a public token, check if it's expired first
  if (!localStorage.getItem('JMLAuthToken') && !isPublicRoute) {
    const path = window.location.pathname;
    const canvasMatch = path.match(/\/canvas\/([^/]+)/);
    const slugOrIdFromUrl = canvasMatch ? canvasMatch[1] : null;
    const isPublic = slugOrIdFromUrl ? isPublicSlug(slugOrIdFromUrl) : false;

    if (isPublic) {
      const expiryTime = localStorage.getItem('JMLPublicTokenExpiry');
      const currentTime = Date.now();
      const expiryMs = expiryTime !== null ? Number(expiryTime) : NaN;
      const isInvalidOrExpiring = !Number.isFinite(expiryMs) || currentTime > (expiryMs - 5000);

      // If token is missing from localStorage or is invalid/within 5 seconds of expiring, refresh it
      if (!localStorage.getItem('JMLPublicToken') || isInvalidOrExpiring) {
        // Clear old token before refresh to avoid sending stale credentials if refresh fails
        localStorage.removeItem('JMLPublicToken');
        localStorage.removeItem('JMLPublicTokenExpiry');
        await fetchAndStorePublicToken();
      }
    }
  }

  // Refresh the user token before it lapses. Access tokens are short-lived, so without this
  // nearly every request would cost an extra 401-then-retry round trip.
  if (!config.skipAuthRefresh && localStorage.getItem('JMLAuthToken') && isAuthTokenExpiring()) {
    await refreshAuthToken();
  }

  const sessionType = localStorage.getItem("SESSION_TYPE");
  let token: string | null = null;

  if (sessionType === "USER") {
    token = localStorage.getItem("JMLAuthToken");
  } else if (sessionType === "PUBLIC") {
    token = localStorage.getItem("JMLPublicToken");
  } else {
    // fallback logic
    token = localStorage.getItem("JMLAuthToken") || localStorage.getItem("JMLPublicToken");
  }

  if (token && config.headers && !config.skipAuthRefresh) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  isDevEnvironment && console.log(config);
  return config;
}, (error: AxiosError) => {
  return Promise.reject(error);
});

// Helper to handle 401 errors specifically
const handle401Error = async (error: AxiosError, originalRequest: CustomInternalAxiosRequestConfig) => {
  originalRequest._retry = true;

  try {
    // Shared with the socket layer, so concurrent 401s and socket expiry cause one refresh call
    const outcome = await refreshAuthTokenWithOutcome();
    if (outcome.status === "refreshed") {
      // Update the original request's auth header and retry
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${outcome.token}`;
      }
      return api(originalRequest);
    }
    // The refresh token was refused (or is gone): the session cannot be renewed. Prompt the user to
    // re-login rather than clearing the session immediately; the logout runs when they acknowledge.
    // An "unavailable" outcome is deliberately not treated this way — the backend was unreachable,
    // which says nothing about the credentials, so this request just fails and callers can retry.
    if (outcome.status === "rejected") {
      notifySessionDisconnected();
    }
  } catch (err) {
    console.error("Error during token refresh handling", err);
  }

  return Promise.reject(error);
};

// Response interceptor for handling 401 Unauthorized responses
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomInternalAxiosRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response ? error.response.status : null;

    if (status === 401 && !originalRequest._retry && !originalRequest.skipAuthRefresh) {
      return handle401Error(error, originalRequest);
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// Project Details & Settings APIs
// =============================================================================

export interface RepoScanCommitInfo {
  repo_url: string;
  commit_id: string;
  commit_timestamp?: string | null;
  branch: string;
  last_scanned_at: string;
  category: string;
  example_code?: string;
}

export interface ProjectDetailsResponse {
  project_id: string;
  project_name: string;
  project_description: string | null;
  created_time: string;
  last_updated: string;
  image_url: string | null;
  code_rescan_mode: CodeRescanMode;
  enable_architecture_doc_report: boolean;
  architecture_doc_view_url: string | null;
}

export interface ResolveProjectBySlugResponse {
  project_id: string;
  slug: string;
  project_name: string;
}

export interface UpdateArchitectureDocReportResponse {
  enable_architecture_doc_report: boolean;
}

/** Fetch user-facing project settings and metadata for a single project. */
export const getProjectDetails = (projectId: string) =>
  api.get<ProjectDetailsResponse>(`/api/projects/${projectId}`);

export interface ShadowV3GraphResponse {
  found: boolean;
  graph: Record<string, unknown> | null;
}

/** Internal/engineering only: fetch the shadow PipelineV3 scan result saved alongside a V2 activity. */
export const getShadowV3Graph = (projectId: string, activityId: string) =>
  api.get<ShadowV3GraphResponse>(
    `/api/projects/${projectId}/shadow-v3/${activityId}`,
  );

export interface ActivityManifestResponse {
  found: boolean;
  manifest: Record<string, unknown> | null;
}

/** Internal/engineering only: fetch the V2 view manifest (code_view/business_view) for a given activity. */
export const getV2ActivityManifest = (projectId: string, activityId: string) =>
  api.get<ActivityManifestResponse>(
    `/api/projects/${projectId}/v2-manifest/${activityId}`,
  );

/** Resolve a canvas slug to a project UUID for the signed-in user. */
export const resolveProjectBySlug = (slug: string) =>
  api.get<ResolveProjectBySlugResponse>("/api/projects/resolve", {
    params: { slug },
  });

/** Enable/disable Google Doc architecture report publishing for a project. */
export const updateArchitectureDocReport = (
  projectId: string,
  enabled: boolean,
) =>
  api.patch<UpdateArchitectureDocReportResponse>(
    `/api/projects/${projectId}/architecture-doc-report`,
    { enabled },
  );

// =============================================================================
// Repository Update Tracking & Commit Diff API Functions
// =============================================================================

/**
 * Check if connected repositories have new commits since last analysis
 * @param projectId - The project ID to check
 * @returns Promise with repo update statuses
 */
export const checkRepoUpdates = (projectId: string) =>
  api.get<CheckRepoUpdatesResponse>(`/api/projects/${projectId}/check-repo-updates`);

/**
 * Get the diff overlay showing which nodes/edges were affected between two refs
 * Works for both commits and branches - backend auto-detects
 * @param projectId - The project ID
 * @param repoUrl - Repository URL to get diff for
 * @param base - Base ref (commit SHA or branch name, e.g., "main" or "abc1234")
 * @param head - Head ref (commit SHA or branch name, e.g., "ai/feature-x" or "def5678")
 * @returns Promise with graph changes ready for handleGraphChanges()
 */
export const getDiff = (
  projectId: string,
  repoUrl: string,
  base: string,
  head: string
) =>
  api.get<CommitDiffResponse>(`/api/projects/${projectId}/diff`, {
    params: {
      repo_url: repoUrl,
      base,
      head,
    },
  });

/**
 * Get the diff overlay for a specific Pull Request
 * @param projectId - The project ID
 * @param repoUrl - Repository URL
 * @param prNumber - Pull request number
 * @returns Promise with graph changes - same structure as getDiff()
 */
export const getPrDiff = (
  projectId: string,
  repoUrl: string,
  prNumber: number
) =>
  api.get<PrDiffResponse>(`/api/projects/${projectId}/pr-diff`, {
    params: {
      repo_url: repoUrl,
      pr_number: prNumber,
    },
  });

/**
 * List PR numbers with persisted diff artifacts already computed for the project.
 * Optionally scope by repository URL.
 */
export const getPersistedPrDiffOptions = (
  projectId: string,
  repoUrl?: string,
) =>
  api.get<PersistedPrDiffOptionsResponse>(
    `/api/projects/${projectId}/pr-diff-options`,
    {
      params: {
        ...(repoUrl ? { repo_url: repoUrl } : {}),
      },
    },
  );

/**
 * Update the code rescan mode for a project.
 * Backend expects body code_rescan_mode as "AUTO" or "MANUAL".
 * @param projectId - The project ID
 * @param mode - The desired rescan mode (CodeRescanMode enum)
 */
export const updateRescanMode = (
  projectId: string,
  mode: CodeRescanMode
) =>
  api.patch<UpdateRescanModeResponse>(`/api/projects/${projectId}/rescan-mode`, {
    code_rescan_mode: mode,
  });

export const updateRepoToken = (projectId: string, repoUrl: string, token: string) =>
  api.patch<UpdateRepoTokenResponse>(`/api/projects/${projectId}/repo-token`, {
    repo_url: repoUrl,
    token,
  });

/**
 * Trigger analysis for a project, optionally for a specific sub-graph drill-down.
 * @param projectId - The project ID
 * @param drillDownNodeId - Optional node ID for sub-graph visualization
 */
export interface AnalyzeResponse {
  success: boolean;
  project_id: string;
  activity_id: string;
  error?: string;
}

export const analyze = (
  projectId: string,
  drillDownNodeId?: string,
  viewType?: string,
  scanMode: ScanMode = ScanMode.IMPATIENT,
) => {
  const formData = buildAnalyzeFormData(
    projectId,
    drillDownNodeId,
    viewType,
    scanMode,
  );
  return api.post<AnalyzeResponse>('/api/analyze', formData);
};

export interface PrefetchLayerResponse {
  success: boolean;
  node_id: string;
  state: 'pending' | 'in_progress' | 'ready' | 'failed' | 'skipped';
  architecture?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  focus?: {
    prefetchable?: boolean;
    skipped?: boolean;
    reason?: string;
    message?: string;
    from_cache?: boolean;
    source_evidence_fallback?: boolean;
  };
}

/**
 * Queue architecture prefetch via ARCH_PREFETCH activity.
 * With wait=true (default), the server blocks until the layer is ready.
 */
export const prefetchArchitectureLayer = (
  projectId: string,
  nodeId: string,
  viewType: 'code' | 'business' | 'top' = 'code',
  parentNodeId?: string,
  wait: boolean = true,
  signal?: AbortSignal,
  graphActivityId?: string,
) =>
  api.post<PrefetchLayerResponse>(
    `/api/projects/${projectId}/prefetch/${encodeURIComponent(nodeId)}`,
    null,
    {
      params: {
        view_type: viewType,
        wait,
        ...(parentNodeId ? { parent_node_id: parentNodeId } : {}),
        ...(graphActivityId ? { graph_activity_id: graphActivityId } : {}),
      },
      timeout: wait ? 600000 : 30000,
      signal,
    },
  );

/**
 * Request the private Pipeline V4 one-level architecture expansion.
 * Its activity binding is intentionally exact and scan mode remains out of the URL.
 */
export const prefetchV4ArchitectureLayer = (
  projectId: string,
  nodeId: string,
  activityId: string,
  signal?: AbortSignal,
) =>
  api.post<PrefetchLayerResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/prefetch/${encodeURIComponent(nodeId)}`,
    null,
    {
      params: {
        activity_id: activityId,
        view_type: 'code',
        wait: true,
      },
      timeout: 600000,
      signal,
    },
  );

/**
 * Upload a zip file and trigger analysis via the HTTP /api/analyze endpoint.
 * Used for zip file uploads instead of the WebSocket code_connect event.
 * @param projectId - The project ID
 * @param file - The zip file (File or Blob)
 * @param filename - The zip file name
 * @param isV2 - Whether to trigger the V2 pipeline
 * @param connectMode - Connection mode (default: "auto")
 */
export const analyzeZipFile = (
  projectId: string,
  file: File | Blob,
  filename: string,
  isV2: boolean = true,
  connectMode: string = 'auto',
  scanMode: ScanMode = ScanMode.IMPATIENT,
) => {
  const formData = buildAnalyzeZipFormData(
    projectId,
    file,
    filename,
    isV2,
    connectMode,
    scanMode,
  );
  return api.post<AnalyzeResponse>('/api/analyze', formData);
};

/**
 * Trigger a manual rescan of connected repositories for a project.
 * Backend endpoint: POST /api/projects/{project_id}/trigger-rescan (no body).
 */
export const triggerRescan = (projectId: string) =>
  api.post<TriggerRescanResponse>(`/api/projects/${projectId}/trigger-rescan`);

export interface AcceptTermsResponse {
  success: boolean;
  has_accepted_terms: boolean;
  message: string;
}

export interface GitHubInstallationMembershipItem {
  installation_id: number;
  group_id: string;
  group_name: string;
  group_type: "personal" | "organization";
  group_status: string;
  billing_plan: string;
  is_member: boolean;
  is_active: boolean;
  role: string | null;
}

export interface GitHubInstallationMembershipsResponse {
  success: boolean;
  installations: GitHubInstallationMembershipItem[];
}

export interface ProjectBillingEntitlementsResponse {
  plan_code: string;
  subscription_state: "inactive" | "active";
  billing_enforcement_enabled: boolean;
  install_context: "personal" | "organization";
}

export interface CreateStripeCheckoutSessionRequest {
  plan_code: string;
  seat_quantity?: number;
  success_url?: string;
  cancel_url?: string;
}

export interface CreateStripeCheckoutSessionResponse {
  success: boolean;
  group_id: string | null;
  plan_code: string;
  seat_quantity: number;
  session_id: string;
  checkout_url: string;
  message: string;
}

export interface CreateStripeBillingPortalSessionResponse {
  success: boolean;
  group_id: string;
  customer_id: string;
  session_id: string;
  portal_url: string;
  message: string;
}

export interface CancelStripeSubscriptionResponse {
  success: boolean;
  group_id: string;
  subscription_id: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  message: string;
}

export interface GroupBillingCancellationStateResponse {
  success: boolean;
  group_id: string;
  subscription_id: string;
  subscription_status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  effective_cancellation_date: string | null;
  message: string;
}

export interface SubscriptionSummaryItem {
  key: string;
  scope: "individual" | "business";
  group_id: string;
  group_type: "personal" | "organization";
  plan_code: string;
  plan_name: string;
  is_paid_plan: boolean;
  subscription_state: string;
  renewal_date: string | null;
  org_name: string | null;
  role: string;
  is_owner_or_admin: boolean;
  managed_by_email: string | null;
}

export interface BillingSubscriptionsOverviewResponse {
  success: boolean;
  individual_subscription: SubscriptionSummaryItem;
  business_subscriptions: SubscriptionSummaryItem[];
  has_active_team_plan: boolean;
  message: string;
}

export interface BillingInvoiceItem {
  invoice_id: string;
  status: string;
  currency: string;
  amount_due_cents: number;
  amount_paid_cents: number;
  created_at: string | null;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
}

export interface GroupBillingInvoicesResponse {
  success: boolean;
  group_id: string;
  invoices: BillingInvoiceItem[];
  message: string;
}

export interface GroupMemberItem {
  user_id: string;
  email: string;
  role: string;
  is_active: boolean;
}

export interface GroupBillingMembersResponse {
  success: boolean;
  group_id: string;
  org_name: string;
  members: GroupMemberItem[];
  max_additional_members_per_request: number;
  message: string;
}

export interface AddGroupMembersRequest {
  member_emails: string[];
}

export interface AddedGroupMemberItem {
  email: string;
  added: boolean;
  message: string;
}

export interface AddGroupMembersResponse {
  success: boolean;
  group_id: string;
  results: AddedGroupMemberItem[];
  message: string;
}

export interface CreateTeamCheckoutSessionRequest {
  org_name: string;
  plan_code: "team_monthly" | "team_annual";
  seat_quantity?: number;
  success_url?: string;
  cancel_url?: string;
}

export const getGitHubInstallationMemberships = () =>
  api.get<GitHubInstallationMembershipsResponse>("/auth/github/installations");

export const getProjectBillingEntitlements = (projectId: string) =>
  api.get<ProjectBillingEntitlementsResponse>(
    `/api/projects/${projectId}/billing/entitlements`,
  );

export const createGroupCheckoutSession = (
  groupId: string,
  body: CreateStripeCheckoutSessionRequest,
) =>
  api.post<CreateStripeCheckoutSessionResponse>(
    `/api/groups/${groupId}/billing/checkout-session`,
    body,
  );

export const createGroupBillingPortalSession = (groupId: string) =>
  api.post<CreateStripeBillingPortalSessionResponse>(
    `/api/groups/${groupId}/billing/invoice-portal-session`,
    {},
  );

export const cancelGroupBillingSubscription = (groupId: string) =>
  api.post<CancelStripeSubscriptionResponse>(`/api/groups/${groupId}/billing/cancel`, {});

export const getGroupBillingCancellationState = (groupId: string) =>
  api.get<GroupBillingCancellationStateResponse>(
    `/api/groups/${groupId}/billing/cancellation-state`,
  );

export const getBillingSubscriptionsOverview = () =>
  api.get<BillingSubscriptionsOverviewResponse>("/api/subscriptions/billing/overview");

export const createTeamCheckoutSession = (body: CreateTeamCheckoutSessionRequest) =>
  api.post<CreateStripeCheckoutSessionResponse>("/api/billing/team/checkout-session", body);

export const getGroupBillingInvoices = (groupId: string, limit: number = 10) =>
  api.get<GroupBillingInvoicesResponse>(`/api/groups/${groupId}/billing/invoices`, {
    params: { limit },
  });

export const getGroupBillingMembers = (groupId: string) =>
  api.get<GroupBillingMembersResponse>(`/api/groups/${groupId}/billing/members`);

export const addGroupBillingMembers = (groupId: string, body: AddGroupMembersRequest) =>
  api.post<AddGroupMembersResponse>(`/api/groups/${groupId}/billing/members`, body);

/** Record that the signed-in user accepted terms and conditions. */
export const acceptTerms = () => api.post<AcceptTermsResponse>("/api/accept-terms");

export default api;
